import { useLocation } from 'react-router-dom';
import { useElements, useStripe } from '@stripe/react-stripe-js';

import useDidMount from '../../../hooks/useDidMount';
import { Currency, StripeCustomerAddress } from '../../../graphql/graphql';
import Amplitude from '../../../configs/amplitude';
import { BaseRootStore } from '../../rootStore/baseRootStore';

export default function useUser(rootStore: BaseRootStore) {
  const stripe = useStripe();
  const elements = useElements();
  const { userStore } = rootStore;

  const location = useLocation();

  useDidMount(() => {
    void userStore.fetchCombinedData();

    // If there's no data in user store,
    // try to read customer id/currency from query params.
    // It's the case when customer data are not yet populated in Stripe.
    if (!userStore.combinedData.stripeCustomer) {
      const queryParams = new URLSearchParams(location.search);
      const stripeCustomerId = queryParams.get('id');
      const currency = queryParams.get('currency');

      userStore.setStripeCustomerFromQueryParams({
        id: stripeCustomerId,
        currency: currency as Currency | null,
      });
    }

    if (userStore.combinedDataLoaded) {
      Amplitude.initUser({
        email: userStore.combinedData.userData?.email,
        id: userStore.combinedData.userData?.uuid,
      });
    }
  });

  const saveCustomerTaxInfo = async () => {
    if (!stripe || !elements) {
      return {
        error: new Error(
          '[useStripeCustomer][saveCustomerTaxInfo] Stripe is not initialized',
        ),
      };
    }

    const addressElementValue = await elements
      .getElement('address')
      ?.getValue();
    let error: Error | null = null;

    if (addressElementValue?.value?.address) {
      const { address } = addressElementValue.value;
      const { name } = addressElementValue.value;
      const customerData: {
        name: string;
        address: StripeCustomerAddress;
        taxIdData?: { id: string; type: string };
      } = {
        name,
        address: {
          city: address.city,
          country: address.country,
          line1: address.line1,
          line2: address.line2,
          postalCode: address.postal_code,
          state: address.state,
        },
      };

      if (userStore.taxId && userStore.selectedCountryInfo?.taxIdType) {
        customerData.taxIdData = {
          id: userStore.taxId,
          type: userStore.selectedCountryInfo.taxIdType,
        };
      }

      const result = await userStore.saveStripeCustomer(customerData);
      error = result.error;
    }

    return { error };
  };

  return {
    saveCustomerTaxInfo,
    customer: userStore.combinedData.stripeCustomer,
    userData: userStore.combinedData.userData,
    isCombinedDataLoaded: userStore.combinedDataLoaded,
    isSaveCustomerLoading: userStore.saveCustomer.loading,
  };
}
